/* eslint-disable */
//  @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-call,@typescript-eslint/restrict-template-expressions
import 'reflect-metadata';
import React, { CSSProperties, JSX, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import s from './Grid.module.less';
import { Spin } from 'antd';
import { observer } from 'mobx-react';
import { FixedSizeList } from 'react-window';
import {
  ColumnInstance,
  IdType,
  Row,
  useColumnOrder,
  useExpanded,
  useFilters,
  useFlexLayout,
  usePagination,
  useRowState,
  useSortBy,
  useTable,
} from 'react-table';
import { isEqual, uniqueId } from 'lodash';
import { GridStore, StateStorage } from './GridStore';
import { usePrevious, useResize, useRowHighlight, useRowSelect } from './hooks';
import { arraysEqual, getUrlGridState, transformStringFromInputNumberFieldToNumber } from './utils';
import ReactDragListView from 'react-drag-listview';
import { throttle } from './debounce';
import { Identifiable, UrlState } from '@custis/common';
import { ExpandMode, SELECTION_COLUMN_ID, TTableColumn } from './models';
import { NEW_ROW_ID } from './constants';
import {
  AdditionalFilter,
  createGridRowData,
  ExpandIcon,
  GridCheckbox,
  GridFilterMenu,
  GridMenuItem,
  GridRow,
  GridRowRenderer,
  HeaderCell,
  IntegratedPaginationWrapper,
  NewRow,
  NoContent,
  Pagination,
  VisibleColumns,
} from './components';
import classNames from 'classnames';
import { DataQuery } from './DataQuery';
import { FilterMapper, SortingMapper } from './mappers';
import { useScrollToHighlightedId } from './hooks/useScrollToHighlightedId';
import { useDeepCompareEffect } from './hooks/useDeepCompare';

export interface GridProps<TItem extends Identifiable, TFilter, TSorting, TResultAdditionalInfo = undefined> {
  hasSelection?: boolean;
  highlightRowOnSelect?: boolean;
  store: GridStore<TItem, TFilter, TSorting, TResultAdditionalInfo>;
  urlState?: StateStorage<DataQuery<TFilter, TSorting>>;
  columns: TTableColumn<TItem, TFilter, TSorting>[];
  expandable?: boolean;
  expandColumnId?: string;
  getSubRow?: (originalRow: TItem, relativeIndex?: number) => TItem[];
  restoreDataState?: boolean;
  checkboxAddon?: (row: TItem) => ReactNode;
  rowCanBeSelected?: (row: TItem) => boolean;
  isVirtualList?: boolean;
  expandReplacement?: 'checkbox';
  paginationHidden?: boolean;
  subRowColumns?: TTableColumn<TItem, TFilter, TSorting>[];
  className?: string;
  columnSettingsHidden?: boolean;
  filtersSettingsVisible?: boolean;
  menuClassName?: string;
  additionalFilters?: AdditionalFilter[];
  getRowStyle?: (originalRow: TItem) => CSSProperties | undefined;

  onCreateRow?(row: TItem): Promise<void>;

  onEditRow?(row: TItem): Promise<void>;

  onCreateSubRow?(row: TItem): Promise<void>;

  onEditSubRow?(row: TItem): Promise<void>;

  onClickRow?(): void;

  onDoubleClickRow?(row: TItem): void;
}

const CheckboxArea = observer(CheckboxAreaForObserver);
const ExpandArea = observer(ExpandAreaForObserver);
const Cell = observer(CellForObserver);

const NEW_ROW_HEIGHT = 50;

export const Grid = observer(
  <TItem extends Identifiable & { children?: TItem[] }, TFilter, TSorting, TResultAdditionalInfo = undefined>(
    props: GridProps<TItem, TFilter, TSorting, TResultAdditionalInfo>,
  ) => {
    const { store, isVirtualList = true, additionalFilters } = props;
    const tableHeaderKey = useMemo(() => uniqueId('header'), []);
    const tableBodyKey = useMemo(() => uniqueId('body'), []);
    const newRowKey = useMemo(() => uniqueId('new-row'), []);
    const fixedSizeListKey = useMemo(() => uniqueId('list'), []);
    const defaultColumn: Partial<TTableColumn<TItem, TFilter, TSorting>> = useMemo(
      () => ({
        minWidth: 30,
        width: 150,
      }),
      [],
    );

    const columns = useMemo(() => props.columns, [props.columns]);
    const data = useMemo(() => store.result.items, [store.result.items]);
    const gridFiltersKeys = useMemo(() => columns.map((item) => item.id), [columns]);
    const highlightedId = useMemo(() => store.gridState.highlightedId, [store.gridState.highlightedId]);
    const fixedSizeListRef = useRef<FixedSizeList>(null);
    const fixedSizeListOuterRef = useRef<HTMLDivElement>(null);
    const nonVirtualListRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const sortingMapper = new SortingMapper();
    const filterMapper = new FilterMapper();
    const prevTableRowMode = usePrevious(store.gridState.tableRowMode);

    useEffect(() => {
      if (columns.length !== store.gridState.columns.length) {
        store.setColumns(columns);
      }
    }, [columns, store.gridState.columns]);

    const subRowIdSeparator = store?.subRowIdSeparator || '-';

    useEffect(() => {
      if (props.subRowColumns) {
        store.gridState.subRowColumns = props.subRowColumns.map((item) => ({
          ...item,
          isVisible: typeof item.isVisible === 'boolean' ? item.isVisible : true,
        }));
      }
    }, [props.subRowColumns]);

    useEffect(() => {
      store.setUrlState(props.urlState ?? UrlState.instance(getUrlGridState(), 'default'));
    }, [props.urlState]);

    const prepareExpandedColumns = (
      columns: ColumnInstance<TItem>[],
      expandColumnId: string | undefined,
    ): ColumnInstance<TItem>[] => {
      if (!expandColumnId) return columns;
      return [
        ...columns.map((col) => {
          if (col.id !== expandColumnId) return col;

          let eCol = { ...col };

          // Если у колонки уже есть Cell — оборачиваем его
          if ('Cell' in eCol && typeof eCol.Cell === 'function') {
            const OriginalCell = eCol.Cell;
            eCol.Cell = (props: any) => {
              const originalElement = <OriginalCell {...props} />;
              return (
                <>
                  {props.row.canExpand ? (
                    <ExpandArea row={props.row} store={store} className={s.InColumnExpand} />
                  ) : (
                    <span style={{ paddingLeft: `${props.row.depth * 2}rem` }} />
                  )}{' '}
                  {originalElement}
                </>
              );
            };
          }

          if ('Header' in eCol) {
            const ExpandComponent = ({
              toggleAllRowsExpanded,
            }: {
              toggleAllRowsExpanded: (value?: boolean) => void;
            }) => (
              <span className={s.InColumnExpand}>
                <ExpandIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    const isCurrentlyOpened = store.gridState.expandedAllMode === ExpandMode.OPENED;
                    toggleAllRowsExpanded(!isCurrentlyOpened);
                    store.toggleAllExpand(store.gridState.expandedAllMode);
                  }}
                  mode={store.gridState.expandedAllMode}
                />
              </span>
            );
            if (typeof eCol.Header === 'function') {
              const OriginalHeader = eCol.Header;
              eCol.Header = (props) => {
                const originalElement = <OriginalHeader {...props} />;
                return (
                  <>
                    <ExpandComponent {...props} />
                    {originalElement}
                  </>
                );
              };
            } else if (typeof eCol.Header === 'string' || typeof eCol.Header === 'number') {
              const OriginalHeader = eCol.Header;
              eCol.Header = (props) => (
                <>
                  <ExpandComponent {...props} />
                  {OriginalHeader}
                </>
              );
            } else if (React.isValidElement(eCol.Header)) {
              const OriginalHeader = eCol.Header;
              eCol.Header = (props) => (
                <>
                  <ExpandComponent {...props} />
                  {OriginalHeader}
                </>
              );
            }
          }

          return eCol;
        }),
      ];
    };

    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      rows,
      totalColumnsWidth,
      prepareRow,
      // paging
      canPreviousPage,
      canNextPage,
      // pageOptions,
      pageCount,
      gotoPage,
      nextPage,
      previousPage,
      setPageSize,
      toggleRowSelected,
      // cols
      allColumns,
      // resizing
      setResizing,
      // columnOrder
      setColumnOrder,
      // sort
      setSortBy,
      // state
      state,
      setHiddenColumns,
      setAllFilters,
    } = useTable(
      {
        columns,
        data,
        defaultColumn,
        getRowId: (row, relativeIndex, parent) => {
          return parent ? `${parent.id}${subRowIdSeparator}${row.id}` : row.id.toString();
        },
        getSubRows: props.getSubRow,
        initialState: {
          pageIndex: store.urlState?.value?.paging?.pageIndex,
          pageSize: 20,
          expanded: store.gridState.expanded,
          selectedRowIds: store.selectedItemsKeys,
          hiddenColumns: store.hiddenColumns,
          columnResizing: store.gridState.outerColumnResizing,
        },
        // pagination settings
        manualPagination: true,
        pageCount: store.gridState.pageCount,
        // sorting settings
        manualSortBy: true,
        // filter settings
        manualFilters: true,
      },
      useFlexLayout,
      useResize,
      useFilters,
      useSortBy,
      useRowState,
      useExpanded,
      usePagination, // must be after sort by
      useRowSelect,
      useRowHighlight,
      useColumnOrder,
      (hooks) => {
        props.hasSelection
          ? hooks.visibleColumns.push((columns) =>
              !store.areAllColumnsHidden
                ? [
                    {
                      id: SELECTION_COLUMN_ID,
                      width: 60,
                      disableFilters: true,
                      disableSortBy: true,
                      disableResizing: true,
                      Header: ({ getToggleAllRowsSelectedProps, toggleAllRowsExpanded }) => {
                        const { onChange, checked, ...rest } = getToggleAllRowsSelectedProps();

                        return (
                          <div className={s.CheckboxArea}>
                            <div className={s.HeaderButtons}>
                              <GridCheckbox
                                {...rest}
                                onChange={(e) => {
                                  if (onChange) {
                                    onChange(e);
                                  }
                                  store.toggleSelectAll((e as React.ChangeEvent<HTMLInputElement>).target.checked);
                                }}
                                checked={props.store.isAllSelected}
                              />
                              {props.getSubRow && props.expandable && !props.expandColumnId && (
                                <div className={s.HeaderExpand}>
                                  <ExpandIcon
                                    onClick={() => {
                                      toggleAllRowsExpanded(!(store.gridState.expandedAllMode === ExpandMode.OPENED));
                                      store.toggleAllExpand(store.gridState.expandedAllMode);
                                    }}
                                    mode={store.gridState.expandedAllMode}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      },
                      Cell: ({ row }: { row: Row<TItem> }) => (
                        <Cell
                          {...{
                            row,
                            store,
                            rowCanBeSelected,
                            expandReplacement: props.expandReplacement,
                            checkboxAddon: props.checkboxAddon,
                            highlightRowOnSelect: props.highlightRowOnSelect,
                            expandColumnId: props.expandColumnId,
                          }}
                        />
                      ),
                    },
                    ...prepareExpandedColumns(columns, props.expandColumnId),
                  ]
                : prepareExpandedColumns(columns, props.expandColumnId),
            )
          : props.expandable
            ? hooks.visibleColumns.push((columns) => prepareExpandedColumns(columns, props.expandColumnId))
            : undefined;
      },
    );
    const { pageIndex, pageSize, sortBy, columnResizing, filters, columnOrder } = state;

    useEffect(() => {
      const cb = throttle(16, (e: Event) => {
        store.scrollLeft = (e.target as HTMLElement).scrollLeft;
        if (headerRef.current) {
          headerRef.current.style.transform = `translateX(-${store.scrollLeft}px)`;
        }
        const newRow = document.getElementById(NEW_ROW_ID);
        if (newRow) {
          newRow.style.transform = `translateX(-${store.scrollLeft}px)`;
        }
      });
      gridAreaRef.current?.addEventListener('scroll', cb, true);
    }, []);

    useEffect(() => {
      const newRow = document.getElementById(NEW_ROW_ID);
      if (newRow && store.gridState.tableRowMode === 'create') {
        newRow.style.transform = `translateX(-${store.scrollLeft}px)`;
        setGridHeight((prev) => prev - newRow.clientHeight);
      }
      if (store.gridState.tableRowMode === 'view' && prevTableRowMode === 'create') {
        setGridHeight((prev) => prev + NEW_ROW_HEIGHT);
      }
    }, [store.gridState.tableRowMode]);

    useEffect(() => {
      // Восстановление скрола после пагинации/фильтрации
      if (fixedSizeListOuterRef.current) {
        fixedSizeListOuterRef.current.scrollLeft = store.scrollLeft;
      }
      if (nonVirtualListRef.current) {
        nonVirtualListRef.current.scrollLeft = store.scrollLeft;
      }
    }, [store.isLoading, rows.length, store.result.items]);

    useEffect(() => {
      if (fixedSizeListRef.current && !store.fixedSizeList) {
        store.fixedSizeList = fixedSizeListRef;
      }
      if (nonVirtualListRef.current && !store.nonVirtualList) {
        store.nonVirtualList = nonVirtualListRef;
      }
    }, [store.result.items]);

    useEffect(() => {
      setHiddenColumns(store.hiddenColumns);
    }, [store.gridState.hiddenColumns]);

    useEffect(() => {
      store.loadCheckedRows();
      store.loadExpandedRows();
      store.loadHiddenColumns();
      store.loadResizing();

      return () => {
        store.resetGrid();
      };
    }, []);

    useEffect(() => {
      store.loadHiddenColumns();
    }, [columns.length]);

    useEffect(() => {
      store.loadOrderColumns(columns, !!props.hasSelection);
    }, [columns]);

    useEffect(() => {
      store.toggleRowSelected = toggleRowSelected;
    }, [toggleRowSelected]);

    const rowCanBeSelected = useCallback(
      (row: TItem): boolean => {
        if (!props.rowCanBeSelected) {
          return true;
        }
        return props.rowCanBeSelected(row);
      },
      [props.rowCanBeSelected],
    );

    useEffect(() => {
      store.rowCanBeSelected = rowCanBeSelected;
    }, [rowCanBeSelected]);

    useEffect(() => {
      store.getSubRow = props.getSubRow;
    }, [props.getSubRow]);

    const gridAreaRef = useRef<HTMLDivElement | null>(null);
    const [gridHeight, setGridHeight] = useState(400);

    useEffect(() => {
      if (gridAreaRef.current != null) {
        setGridHeight(!!gridAreaRef.current ? gridAreaRef.current.clientHeight - 100 : 400);
      }
      window.addEventListener('resize', () => {
        if (gridAreaRef.current != null) {
          setGridHeight(!!gridAreaRef.current ? gridAreaRef.current.clientHeight - 100 : 400);
        }
      });
    }, [gridAreaRef.current]);

    useEffect(() => {
      if (store.stateRestored) {
        store.setFilters(filters);
      }
    }, [filters]);

    useEffect(() => {
      if (store.stateRestored && store.urlState?.value?.filter) {
        const gridFilters = filterMapper
          .mapToGrid(store.urlState?.value?.filter, store.filterMapping)
          .filter((item) => gridFiltersKeys.includes(item.id) && item.value !== undefined);
        if (!isEqual(gridFilters, filters)) {
          setAllFilters(gridFilters);
        }
      }
    }, [store.urlState?.value?.filter, filters]);

    useDeepCompareEffect(() => {
      const state = store.urlState?.value;

      if (state) {
        if (state.sorting) {
          setSortBy(sortingMapper.mapToGrid(state.sorting, store.sortingMapping));
        }
        if (state.paging) {
          gotoPage(state.paging.pageIndex);
          setPageSize(state.paging.top);
        }
        const gridFilters = filterMapper
          .mapToGrid(state.filter, store.filterMapping)
          .filter((item) => gridFiltersKeys.includes(item.id));
        setAllFilters(gridFilters);

        void store.loadData().then(() => {
          store.stateRestored = true;
        });
      }
    }, [gridFiltersKeys]);

    useEffect(() => {
      if (store.stateRestored) {
        store.setSorting(sortBy);
      }
    }, [sortBy]);

    useEffect(() => {
      if (store.stateRestored) {
        store.setPaging(pageSize, pageIndex);
      }
    }, [pageIndex, pageSize]);

    useEffect(() => {
      if (pageCount < pageIndex + 1 && store.stateRestored) {
        store.setPaging(pageSize, pageIndex);
        gotoPage(0);
      }
    }, [pageIndex, pageCount, pageSize]);

    useEffect(() => {
      if ([ExpandMode.CLOSED, ExpandMode.OPENED].includes(store.gridState.expandedAllMode)) {
        store.result.items.forEach((item) => {
          store.gridState.expanded = {
            ...store.gridState.expanded,
            [item.id]: store.gridState.expandedAllMode === ExpandMode.OPENED,
          };
          store.saveExpanded(store.gridState.expanded);
        });
      }
    }, [pageIndex, pageCount, pageSize, store.result.items]);

    useEffect(() => {
      const allOpened = Object.values(store.gridState.expanded).every((item) => item);
      const allClosed = Object.values(store.gridState.expanded).every((item) => !item);

      if ([ExpandMode.OPENED, ExpandMode.MIXED].includes(store.gridState.expandedAllMode)) {
        store.gridState.expandedAllMode = !allOpened && !allClosed ? ExpandMode.MIXED : ExpandMode.OPENED;
      }
      if ([ExpandMode.CLOSED, ExpandMode.MIXED].includes(store.gridState.expandedAllMode)) {
        store.gridState.expandedAllMode = !allClosed && !allOpened ? ExpandMode.MIXED : ExpandMode.CLOSED;
      }
      localStorage.setItem(store.expandAllKey, store.gridState.expandedAllMode);
    }, [store.result.items, store.gridState.expanded, store.gridState.expandedAllMode]);

    useEffect(() => {
      if (!arraysEqual(store.gridState.columnOrder, columnOrder)) {
        setColumnOrder(store.gridState.columnOrder);
      }
    }, [store.gridState.columnOrder]);

    useEffect(() => {
      if (store.gridState.outerColumnResizing) {
        setResizing(store.gridState.outerColumnResizing);
      }
    }, [store.gridState.outerColumnResizing]);

    useEffect(() => {
      store.setColumnsResizing(columnResizing);
    }, [columnResizing]);

    const itemHeight = useMemo(() => 50, []);

    useEffect(() => {
      store.pagingService.setPaginationPropsActions({
        gotoPage,
        previousPage,
        nextPage,
        setPageSize,
      });
    }, []);

    useEffect(() => {
      store.pagingService.setPaginationPropsData({
        canNextPage,
        canPreviousPage,
        pageSize,
        pageCount,
        pageIndex,
      });
    }, [canNextPage, canPreviousPage, pageSize, pageCount, pageIndex]);

    useEffect(() => {
      if (store.urlState?.value?.selection?.highlightedRowId) {
        store.setHighlightedRowId(store.urlState?.value?.selection?.highlightedRowId);
      }
    }, [store.urlState?.value?.selection?.highlightedRowId]);

    useEffect(() => {
      if (store.gridState.highlightedId) {
        store.setHighlightedRowId(store.gridState.highlightedId);
      }
    }, [store.gridState.highlightedId]);

    const handleCreateRow = useCallback((row: TItem): Promise<void> => {
      if (props.onCreateRow) {
        return props.onCreateRow(transformStringFromInputNumberFieldToNumber(row, allColumns));
      }
      return Promise.resolve();
    }, []);

    const handleEditRow = useCallback((row: TItem): Promise<void> => {
      if (props.onEditRow) {
        return props.onEditRow(transformStringFromInputNumberFieldToNumber(row, allColumns));
      }
      return Promise.resolve();
    }, []);

    const handleCreateSubRow = useCallback((row: TItem): Promise<void> => {
      if (props.onCreateSubRow) {
        return props.onCreateSubRow(transformStringFromInputNumberFieldToNumber(row, store.gridState.columns));
      }
      return Promise.resolve();
    }, []);

    const handleEditSubRow = useCallback((row: TItem): Promise<void> => {
      if (props.onEditSubRow) {
        return props.onEditSubRow(transformStringFromInputNumberFieldToNumber(row, store.gridState.columns));
      }
      return Promise.resolve();
    }, []);

    const onClickRow = useCallback(
      (id: string) => {
        if (props.onClickRow) {
          props.onClickRow();
        }
        if (!['create-sub-row', 'create'].includes(store.gridState.tableRowMode)) {
          store.gridState.highlightedId = id;
          if (id !== highlightedId) {
            store.changeTableMode('view');
          }
        }
      },
      [store.gridState.tableRowMode],
    );

    const onRowDoubleClick = useCallback(
      (row: TItem, gridRowId: string) => {
        props.onDoubleClickRow?.(row);

        const depth = rows.find((item) => {
          // формат id child строк - {parentId}-{childId}
          if (gridRowId.includes(subRowIdSeparator)) {
            return gridRowId === item.id;
          } else {
            return item.id === row.id.toString();
          }
        })?.depth;
        const isAtLeastOneColumnEditable = columns.some((item) =>
          typeof item.editable === 'function' ? item.editable(row) : item.editable,
        );

        if (props.onEditRow && depth === 0 && isAtLeastOneColumnEditable) {
          store.changeTableMode('edit');
          store.editRowData = row;
        }
        if (props.onEditSubRow && depth === 1 && isAtLeastOneColumnEditable) {
          store.changeTableMode('edit-sub-row');
          store.editRowData = row;
        }
      },
      [rows],
    );

    const getRowStyle = useCallback((originalRow: TItem) => props.getRowStyle?.(originalRow), []);

    const gridRowData = createGridRowData(
      rows,
      highlightedId,
      store.gridState.tableRowMode,
      totalColumnsWidth,
      allColumns,
      store.editRowData,
      prepareRow,
      onClickRow,
      onRowDoubleClick,
      handleEditRow,
      (row: TItem) => store.onEditRowChange(row),
      () => store.onHideEditRow(),
      store.newRowData,
      store.gridState.subRowColumns,
      handleCreateSubRow,
      (row: TItem) => store.onNewRowChange(row),
      store.gridState.subRowId,
      handleEditSubRow,
      store.hiddenColumns,
      getRowStyle,
    );

    const resetHighlightedRowId = useCallback(() => {
      void store.setHighlightedRowId(undefined);
    }, [store]);

    useScrollToHighlightedId<TItem>({
      rows,
      highlightedId,
      itemHeight,
      highlightedRowIdOnMount: props.urlState?.value?.selection?.highlightedRowId,
      resetHighlightedRowId,
      fixedSizeListRef,
      nonVirtualListRef,
      isVirtualList,
    });

    return (
      <div className={classNames(s.GridArea, props.className)} ref={gridAreaRef}>
        <Spin spinning={store.isLoading} wrapperClassName={classNames(s.SpinPanel)}>
          <div className={classNames(s.GridMenu, props.menuClassName)}>
            {!props.columnSettingsHidden ? (
              <GridMenuItem>
                <VisibleColumns
                  columns={allColumns}
                  hiddenColumns={store.hiddenColumns}
                  isColumnVisible={(id) => store.getColumnVisibility(id)?.visible}
                  toggleHidden={(id, visible, touched) => store.toggleHiddenColumn(id, visible, touched)}
                  toggleAll={(value) => store.toggleAllHiddenColumns(value)}
                />
              </GridMenuItem>
            ) : null}
            {props.filtersSettingsVisible ? (
              <GridFilterMenu columns={allColumns} store={store} additionalFilters={additionalFilters} />
            ) : null}
          </div>
          <ReactDragListView.DragColumn
            onDragEnd={(from: number, to: number) => store.onDragEnd(from, to)}
            nodeSelector={'.GridHeaderCell'}
            handleSelector={'.GridDrag'}
            ignoreSelector={'.Addons'}
          >
            <div {...getTableProps()} className={s.Grid} key={tableHeaderKey}>
              <div className={s.HeaderWrapper}>
                <div style={{ width: totalColumnsWidth }} ref={headerRef}>
                  {headerGroups.map((headerGroup, index) => (
                    <div {...headerGroup.getHeaderGroupProps()} key={index} className={s.GridHeaderGroup}>
                      {headerGroup.headers.map((column) => (
                        <HeaderCell key={column.id} column={column} />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
              <div {...getTableBodyProps()} key={tableBodyKey} className={s.BodyWrapper}>
                {store.gridState.tableRowMode === 'create' && (
                  <NewRow<TItem, TFilter, TSorting>
                    key={newRowKey}
                    visible={store.gridState.tableRowMode === 'create'}
                    columns={allColumns}
                    onCreate={handleCreateRow}
                    onHide={() => store.onHideEditRow()}
                    data={store.newRowData}
                    onFormChange={(row) => store.onNewRowChange(row)}
                    width={totalColumnsWidth}
                    hiddenColumns={store.hiddenColumns}
                  />
                )}
                {store.isLoading ? (
                  <NoContent gridHeight={gridHeight} totalColumnsWidth={totalColumnsWidth} mode={'loading'} />
                ) : rows.length !== 0 ? (
                  isVirtualList ? (
                    <FixedSizeList
                      ref={fixedSizeListRef}
                      className={'fixed-list'}
                      height={gridHeight}
                      itemCount={rows.length}
                      itemSize={itemHeight}
                      width={'100%'}
                      outerRef={fixedSizeListOuterRef}
                      key={fixedSizeListKey}
                      itemKey={(index) => rows[index].id}
                      itemData={gridRowData}
                      overscanCount={5}
                    >
                      {GridRowRenderer}
                    </FixedSizeList>
                  ) : (
                    <div style={{ height: gridHeight, overflow: 'auto' }} ref={nonVirtualListRef}>
                      <div style={{ width: totalColumnsWidth }}>
                        {rows.map((row) => {
                          prepareRow(row);
                          const rowStyle = getRowStyle?.(row.original) ?? {};
                          const rowProps = row.getRowProps();
                          rowProps.style = {
                            ...rowProps.style,
                            width: undefined,
                            ...rowStyle,
                          };
                          const rowHighlighted = row.id === highlightedId;
                          const editEnabled = rowHighlighted && store.gridState.tableRowMode === 'edit';
                          return (
                            <div
                              onDoubleClick={() => onRowDoubleClick(row.original, row.id)}
                              {...rowProps}
                              className={classNames(s.GridRow, rowProps.className)}
                              key={row.id}
                              onClick={() => onClickRow(row.id)}
                            >
                              <GridRow
                                row={row}
                                allColumns={allColumns}
                                editEnabled={editEnabled}
                                onEditRow={handleEditRow}
                                onFormChange={(row) => store.onEditRowChange(row)}
                                onHideEditRow={() => store.onHideEditRow()}
                                editedRow={store.editRowData}
                                hiddenColumns={store.hiddenColumns}
                                subRowIdSeparator={subRowIdSeparator}
                              />
                              <div
                                className={classNames(s.Substrate, rowHighlighted && s.SubstrateHighlighted)}
                                style={{ width: totalColumnsWidth }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )
                ) : (
                  <div style={{ height: gridHeight, overflow: 'auto' }}>
                    <div style={{ width: totalColumnsWidth }}>
                      <NoContent
                        gridHeight={gridHeight}
                        totalColumnsWidth={totalColumnsWidth}
                        mode={
                          Object.keys(store.urlState?.value?.filter ?? {}).length === 0
                            ? 'empty-no-filters'
                            : 'empty-with-filters'
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </ReactDragListView.DragColumn>
          {!props.paginationHidden && (
            <IntegratedPaginationWrapper>
              <Pagination
                gotoPage={gotoPage}
                canNextPage={canNextPage}
                canPreviousPage={canPreviousPage}
                pageSize={pageSize}
                previousPage={previousPage}
                nextPage={nextPage}
                pageCount={pageCount}
                pageIndex={pageIndex}
                setPageSize={setPageSize}
              />
            </IntegratedPaginationWrapper>
          )}
        </Spin>
      </div>
    );
  },
);

function CheckboxAreaForObserver<
  TItem extends Identifiable,
  TFilter,
  TSorting,
  TResultAdditionalInfo = undefined,
>(props: {
  row: Row<TItem>;
  checkboxAddon?: GridProps<TItem, TFilter, TSorting, TResultAdditionalInfo>['checkboxAddon'];
  store: GridProps<TItem, TFilter, TSorting, TResultAdditionalInfo>['store'];
  highlightRowOnSelect?: GridProps<TItem, TFilter, TSorting, TResultAdditionalInfo>['highlightRowOnSelect'];
}): JSX.Element {
  const { row, store, checkboxAddon, highlightRowOnSelect } = props;
  const { onChange, checked, ...rest } = row.getToggleRowSelectedProps();
  return (
    <div className={s.CheckboxArea}>
      {checkboxAddon ? checkboxAddon(row.original) : null}
      <GridCheckbox
        {...rest}
        onChange={(e) => {
          if (onChange) {
            onChange(e);
          }
          store.setSelection(row.id, !row.isSelected);

          if (highlightRowOnSelect) {
            store.gridState.highlightedId = row.id;
          }
        }}
        checked={row.isSelected}
      />
    </div>
  );
}

function ExpandAreaForObserver<
  TItem extends Identifiable,
  TFilter,
  TSorting,
  TResultAdditionalInfo = undefined,
>(props: {
  row: Row<TItem>;
  store: GridProps<TItem, TFilter, TSorting, TResultAdditionalInfo>['store'];
  className?: string;
}): JSX.Element {
  const { row, store, className } = props;
  const onClick = useCallback(() => {
    row.toggleRowExpanded(!row.isExpanded);
    store.setExpanded(parseInt(row.id, 10), !row.isExpanded);
  }, [row.isExpanded, row.id]);
  return (
    <span
      className={className || s.ExpandArea}
      style={{
        paddingLeft: `${row.depth * 2}rem`,
      }}
    >
      <ExpandIcon onClick={onClick} mode={row.isExpanded ? ExpandMode.OPENED : ExpandMode.CLOSED} />
    </span>
  );
}

function CellForObserver<TItem extends Identifiable, TFilter, TSorting, TResultAdditionalInfo = undefined>(
  props: {
    row: Row<TItem>;
    store: GridProps<TItem, TFilter, TSorting, TResultAdditionalInfo>['store'];
    checkboxAddon: GridProps<TItem, TFilter, TSorting, TResultAdditionalInfo>['checkboxAddon'];
    expandReplacement: GridProps<TItem, TFilter, TSorting, TResultAdditionalInfo>['expandReplacement'];
    highlightRowOnSelect?: GridProps<TItem, TFilter, TSorting, TResultAdditionalInfo>['highlightRowOnSelect'];
    expandColumnId?: string;
  } & Required<{ rowCanBeSelected: GridProps<TItem, TFilter, TSorting, TResultAdditionalInfo>['rowCanBeSelected'] }>,
) {
  const { row, store, checkboxAddon, expandReplacement, rowCanBeSelected, highlightRowOnSelect, expandColumnId } =
    props;
  if (row.canExpand && !expandColumnId) {
    if (expandReplacement === 'checkbox') {
      if (rowCanBeSelected && rowCanBeSelected(row.original)) {
        return <CheckboxArea {...{ row, store, checkboxAddon }} />;
      } else {
        return <div className={s.CheckboxArea}>{checkboxAddon ? checkboxAddon(row.original) : null}</div>;
      }
    } else {
      return <ExpandArea {...{ row, store }} />;
    }
  } else {
    if (rowCanBeSelected && rowCanBeSelected(row.original)) {
      return <CheckboxArea {...{ row, store, checkboxAddon, highlightRowOnSelect }} />;
    } else {
      return <div className={s.CheckboxArea}>{checkboxAddon ? checkboxAddon(row.original) : null}</div>;
    }
  }
}
